import React, { useEffect, useRef } from 'react';

const GridBackground = () => {
  const canvasRef = useRef(null);
  const animationId = useRef(null);
  const mouseRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const timeRef = useRef(0);
  const particles = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Initialize floating particles
    const initializeParticles = () => {
      particles.current = [];
      const particleCount = Math.floor((canvas.width * canvas.height) / 25000); // Reduced particle count
      
      for (let i = 0; i < particleCount; i++) {
        particles.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.3, // Slower movement
          vy: (Math.random() - 0.5) * 0.3, // Slower movement
          size: Math.random() * 1.5 + 0.3, // Smaller particles
          opacity: Math.random() * 0.3 + 0.05, // Lower opacity
          pulse: Math.random() * Math.PI * 2
        });
      }
    };
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initializeParticles();
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Mouse tracking
    const handleMouseMove = (event) => {
      mouseRef.current.x = event.clientX;
      mouseRef.current.y = event.clientY;
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Grid configuration
    const gridSize = 50; // Larger grid spacing
    const maxOpacity = 0.4; // Reduced max opacity
    const fadeDistance = 400; // Smaller interaction radius
    const baseOpacity = 0.02; // Very subtle base grid
    const pulseSpeed = 0.015; // Slower pulse animation
    
    // Create gradient for enhanced visual effect
    const createRadialGradient = (x, y, radius, opacity) => {
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      gradient.addColorStop(0, `rgba(99, 102, 241, ${opacity})`);
      gradient.addColorStop(0.3, `rgba(59, 130, 246, ${opacity * 0.7})`);
      gradient.addColorStop(0.6, `rgba(139, 92, 246, ${opacity * 0.4})`);
      gradient.addColorStop(1, 'rgba(99, 102, 241, 0)');
      return gradient;
    };
    
    const drawGrid = () => {
      timeRef.current += pulseSpeed;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const mouse = mouseRef.current;
      
      // Update and draw floating particles
      particles.current.forEach(particle => {
        // Update particle position
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.pulse += 0.05;
        
        // Wrap around screen edges
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
        
        // Calculate distance from mouse
        const distanceFromMouse = Math.sqrt(
          Math.pow(mouse.x - particle.x, 2) + Math.pow(mouse.y - particle.y, 2)
        );
        
        // Mouse interaction effect
        const mouseInfluence = Math.max(0, 1 - (distanceFromMouse / 150)); // Smaller influence radius
        const finalOpacity = particle.opacity + (mouseInfluence * 0.15); // Reduced opacity boost
        const size = particle.size + (mouseInfluence * 1); // Smaller size increase
        
        // Draw particle with pulsing effect
        const pulseOpacity = finalOpacity * (0.7 + 0.3 * Math.sin(particle.pulse));
        ctx.fillStyle = `rgba(99, 102, 241, ${pulseOpacity})`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, size, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw particle glow
        if (mouseInfluence > 0.05) { // Higher threshold for glow
          ctx.fillStyle = createRadialGradient(particle.x, particle.y, size * 2, mouseInfluence * 0.1); // Smaller glow
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, size * 2, 0, Math.PI * 2); // Smaller glow radius
          ctx.fill();
        }
      });
      
      // Draw enhanced grid with wave effects
      const waveAmplitude = 0.05; // Reduced wave effect
      const waveFrequency = 0.008; // Slower wave frequency
      
      // Draw grid intersections with enhanced effects
      for (let x = 0; x <= canvas.width; x += gridSize) {
        for (let y = 0; y <= canvas.height; y += gridSize) {
          // Calculate distance from mouse to this grid intersection
          const distanceFromMouse = Math.sqrt(
            Math.pow(mouse.x - x, 2) + Math.pow(mouse.y - y, 2)
          );
          
          // Add wave effect based on time and distance
          const wave = Math.sin(timeRef.current * 2 + distanceFromMouse * waveFrequency) * waveAmplitude;
          
          // Calculate opacity with wave effect
          const proximityOpacity = Math.max(0, (maxOpacity + wave) - (distanceFromMouse / fadeDistance));
          const finalOpacity = Math.max(baseOpacity, proximityOpacity);
          
          if (finalOpacity > baseOpacity * 2) {
            // Enhanced line thickness based on proximity
            const lineWidth = finalOpacity > 0.3 ? 2 : finalOpacity > 0.15 ? 1.5 : 0.8;
            
            // Create gradient effect for lines near mouse
            if (proximityOpacity > 0.1) {
              ctx.strokeStyle = createRadialGradient(x, y, gridSize * 2, finalOpacity);
            } else {
              ctx.strokeStyle = `rgba(99, 102, 241, ${finalOpacity})`;
            }
            
            ctx.lineWidth = lineWidth;
            
            // Draw vertical line segment with slight curve
            ctx.beginPath();
            const startY = Math.max(0, y - gridSize/2);
            const endY = Math.min(canvas.height, y + gridSize/2);
            const curveOffset = wave * 5;
            
            ctx.moveTo(x + curveOffset, startY);
            ctx.quadraticCurveTo(x, y, x - curveOffset, endY);
            ctx.stroke();
            
            // Draw horizontal line segment with slight curve
            ctx.beginPath();
            const startX = Math.max(0, x - gridSize/2);
            const endX = Math.min(canvas.width, x + gridSize/2);
            
            ctx.moveTo(startX, y + curveOffset);
            ctx.quadraticCurveTo(x, y, endX, y - curveOffset);
            ctx.stroke();
            
            // Draw intersection point with enhanced glow
            if (proximityOpacity > 0.15) { // Higher threshold
              const pointSize = 0.5 + proximityOpacity * 1; // Smaller intersection points
              ctx.fillStyle = `rgba(99, 102, 241, ${proximityOpacity * 0.6})`; // Reduced opacity
              ctx.beginPath();
              ctx.arc(x, y, pointSize, 0, Math.PI * 2);
              ctx.fill();
              
              // Add glow effect
              ctx.fillStyle = createRadialGradient(x, y, pointSize * 3, proximityOpacity * 0.2); // Smaller glow
              ctx.beginPath();
              ctx.arc(x, y, pointSize * 3, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        }
      }
      
      // Draw subtle base grid (very faint everywhere)
      ctx.strokeStyle = `rgba(99, 102, 241, ${baseOpacity})`;
      ctx.lineWidth = 0.3;
      
      // Vertical lines with subtle animation
      for (let x = 0; x <= canvas.width; x += gridSize) {
        const waveOffset = Math.sin(timeRef.current + x * 0.01) * 0.5;
        ctx.globalAlpha = baseOpacity + Math.abs(waveOffset) * 0.02;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      
      // Horizontal lines with subtle animation
      for (let y = 0; y <= canvas.height; y += gridSize) {
        const waveOffset = Math.sin(timeRef.current + y * 0.01) * 0.5;
        ctx.globalAlpha = baseOpacity + Math.abs(waveOffset) * 0.02;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
      
      // Reset global alpha
      ctx.globalAlpha = 1;
      
      // Draw mouse glow effect
      const glowRadius = 100; // Smaller glow radius
      const glowOpacity = 0.05; // Reduced glow opacity
      ctx.fillStyle = createRadialGradient(mouse.x, mouse.y, glowRadius, glowOpacity);
      ctx.beginPath();
      ctx.arc(mouse.x, mouse.y, glowRadius, 0, Math.PI * 2);
      ctx.fill();
      
      animationId.current = requestAnimationFrame(drawGrid);
    };
    
    // Start animation
    drawGrid();
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationId.current) {
        cancelAnimationFrame(animationId.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="grid-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
        background: 'transparent'
      }}
    />
  );
};

export default GridBackground;
